import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  CardActions,
  Grid,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  MenuItem,
  Divider,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SettingsInputAntennaIcon from '@mui/icons-material/SettingsInputAntenna';
import Hls from 'hls.js';
import { useAuth } from '../../../contexts/AuthContext';
import { TextField } from '@mui/material';

interface LiveStreamTabProps {
  deviceId: string;
}

interface StreamInfo {
  isLive: boolean;
  hlsUrl: string;
  whepUrl: string;
  streamKey: string;
  // Which of three ways this device's stream is sourced. Replaces a former
  // plain isOnDemand boolean, which only ever gated one thing (skipping the
  // is-live poll for RTSP-pull devices) — fully expressible as
  // sourceMode === 'direct-rtsp', so keeping both risked drift.
  //  - 'direct-rtsp': camera runs its own RTSP server, MediaMTX pulls on
  //    demand. Always "available" — no publish-time signal to poll.
  //  - 'pi5-relay': a battery-powered Pi5 pulls RTSP from the camera on its
  //    own local LAN and pushes RTMP to us — can't stream 24/7, so Watch
  //    Live has to explicitly start it and wait (see handleWatchPi5Relay),
  //    rather than MediaMTX pulling on demand like direct-rtsp.
  //  - 'rtmp-key': classic push flow — camera/drone publishes to us using
  //    its own DeviceStreamKey, whenever it's actually turned on.
  sourceMode: 'direct-rtsp' | 'pi5-relay' | 'rtmp-key';
  // Only set for the RTMP flow (sourceMode === 'rtmp-key') — the URL to
  // type into an actual RTMP publisher (camera/drone encoder), e.g.
  // rtmp://rtmp.dmotrak.com/live/{streamKey}. direct-rtsp/pi5-relay devices
  // have no equivalent — nothing needs typing, MediaMTX or the Pi5 already
  // knows where to pull/push.
  rtmpUrl: string | null;
}

interface Recording {
  name: string;
  url: string;
  size: number;
  modified: string;
}

const HLS_BASE = '/live-hls';
const WHEP_BASE = '/live-whep';
// Public RTMP ingest domain — what an actual camera/drone encoder needs to
// publish to (see dmo-edge's rtmp-server, reachable from anywhere, not just
// the LAN). Reused verbatim in the copyable "RTMP Address" field below.
const RTMP_PUBLIC_HOST = 'rtmp.dmotrak.com';

// Hand-rolled WHEP client (WebRTC playback of a MediaMTX path) — no
// signaling library needed, just a POST of a complete SDP offer and a
// text/sdp answer back. Waiting for ICE gathering to finish before posting
// means the offer already carries every candidate, so there's no need to
// implement WHEP's trickle-ICE PATCH flow — at the cost of a small join
// delay, bounded by the timeout below.
async function playWhep(video: HTMLVideoElement, whepUrl: string): Promise<RTCPeerConnection> {
  const pc = new RTCPeerConnection();
  pc.addTransceiver('video', { direction: 'recvonly' });
  pc.addTransceiver('audio', { direction: 'recvonly' });

  const stream = new MediaStream();
  pc.ontrack = (event) => {
    stream.addTrack(event.track);
    video.srcObject = stream;
  };

  const offer = await pc.createOffer();
  await pc.setLocalDescription(offer);

  await new Promise<void>((resolve) => {
    if (pc.iceGatheringState === 'complete') {
      resolve();
      return;
    }
    const onChange = () => {
      if (pc.iceGatheringState === 'complete') {
        pc.removeEventListener('icegatheringstatechange', onChange);
        resolve();
      }
    };
    pc.addEventListener('icegatheringstatechange', onChange);
    setTimeout(resolve, 3000);
  });

  const res = await fetch(whepUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/sdp' },
    body: pc.localDescription?.sdp ?? '',
  });
  if (!res.ok) {
    pc.close();
    throw new Error(`WHEP negotiation failed (${res.status})`);
  }
  const answerSdp = await res.text();
  await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });
  return pc;
}

const LiveStreamTab: React.FC<LiveStreamTabProps> = ({ deviceId }) => {
  const auth = useAuth();
  const accessToken = auth.tokens?.accessToken;
  const liveVideoRef = useRef<HTMLVideoElement>(null);
  const recordingVideoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  // Backstop for the WHEP→HLS fallback below — see startLivePlayback.
  const whepFallbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Next-scheduled poll for the pi5-relay "wake and wait for live" flow —
  // see startRelayWaitForLive.
  const relayPollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Mirror streamInfo/isLivePlaying state into refs purely so the unmount
  // cleanup effect below (empty dep array, runs once at mount) can read
  // their live value instead of whatever they were at mount time — a plain
  // closure over the state variables would be stale by the time a real
  // unmount happens.
  const streamInfoRef = useRef<StreamInfo | null>(null);
  const isLivePlayingRef = useRef(false);
  // Object URL backing the currently-playing recording's <video src> — see
  // handlePlayRecording for why this exists instead of a plain rec.url.
  // Tracked so it can be revoked (URL.revokeObjectURL) once no longer
  // needed instead of leaking the underlying blob.
  const recordingObjectUrlRef = useRef<string | null>(null);

  const [subTab, setSubTab] = useState(0);
  const [streamInfo, setStreamInfo] = useState<StreamInfo | null>(null);
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isLivePlaying, setIsLivePlaying] = useState(false);
  const [playingRecording, setPlayingRecording] = useState<string | null>(null);
  // True while a recording's full blob is downloading before playback can
  // start — the whole file has to land before URL.createObjectURL has
  // anything to point at (no progressive/streamed playback with this
  // approach), which can take a while for a large recording with no other
  // visual feedback otherwise.
  const [loadingRecording, setLoadingRecording] = useState(false);
  const [downloadingRecording, setDownloadingRecording] = useState<string | null>(null);
  const [deletingRecording, setDeletingRecording] = useState<string | null>(null);
  // Editable RTSP source URL (admin-only write, via PUT/DELETE
  // /api/stream/rtsp-source/{deviceId}). Kept separate from streamInfo
  // above — streamInfo/loadStreamInfo is polled every 10s and would
  // otherwise stomp on whatever an admin is mid-typing into this field.
  const [rtspUrlInput, setRtspUrlInput] = useState('');
  const [rtspSaving, setRtspSaving] = useState(false);
  // Which device (if any) relays this camera's RTSP feed to us as RTMP —
  // same "kept separate from the 10s-polled streamInfo" reasoning as
  // rtspUrlInput above. relayOptions is the candidate list for the picker,
  // loaded once (every device except this one — see loadRelayOptions).
  const [relayDeviceId, setRelayDeviceId] = useState('');
  const [relayOptions, setRelayOptions] = useState<{ id: string; name: string }[]>([]);
  const [relaySaving, setRelaySaving] = useState(false);
  // True from the moment Watch Live sends the pi5-relay start signal until
  // either the wait-for-live poll succeeds or its timeout gives up — see
  // startRelayWaitForLive.
  const [relayWaking, setRelayWaking] = useState(false);

  useEffect(() => { streamInfoRef.current = streamInfo; }, [streamInfo]);
  useEffect(() => { isLivePlayingRef.current = isLivePlaying; }, [isLivePlaying]);

  // Shared by both flows where MediaMTX's "live/{streamKey}" path is the
  // actual source of truth (classic RTMP push, and pi5-relay — the Pi5
  // publishes RTMP using the same DeviceStreamKey a real camera/drone
  // would). pi5-relay is treated as always "available" the same way
  // direct-rtsp is below — Watch Live is what actually makes it go live
  // (POST .../start, then wait — see startRelayWaitForLive), so there's no
  // publish-time signal worth gating the button on beforehand, unlike
  // rtmp-key mode where a real camera/drone either already is or isn't
  // actively pushing to us right now, independent of anything clicked here.
  const loadRtmpBackedStreamInfo = useCallback(async (mode: 'pi5-relay' | 'rtmp-key') => {
    const res = await fetch(`/api/devices/${deviceId}/stream-key`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!res.ok) throw new Error('Failed to load stream key');
    const { keyValue: streamKey } = await res.json();

    // MediaMTX has no separate app/key split — "live/" here has to match
    // whatever leading path segment publishers actually send (see
    // StreamController.MediaMtxAuth's comment), otherwise this resolves
    // to a different MediaMTX path than the one actually being recorded.
    const hlsUrl = `${HLS_BASE}/live/${streamKey}/index.m3u8`;
    const whepUrl = `${WHEP_BASE}/live/${streamKey}/whep`;

    let isLive = mode === 'pi5-relay';
    if (mode === 'rtmp-key') {
      // MediaMTX's on-demand LL-HLS muxer can't be probed directly for
      // liveness: a HEAD request always 404s (the cookie-check/session
      // logic that actually resolves liveness only runs on GET), and a GET
      // blocks for the muxer's whole lifetime (long-poll semantics) rather
      // than returning promptly — unusable on a 10s poll. Ask the backend,
      // which checks MediaMTX's control API instead (StreamController.IsLive).
      try {
        const liveRes = await fetch(`/api/stream/is-live/${deviceId}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (liveRes.ok) {
          isLive = (await liveRes.json()).isLive === true;
        }
      } catch {
        // Not live
      }
    }

    const rtmpUrl = mode === 'rtmp-key' ? `rtmp://${RTMP_PUBLIC_HOST}/live/${streamKey}` : null;
    setStreamInfo({ isLive, sourceMode: mode, hlsUrl, whepUrl, streamKey, rtmpUrl });
  }, [deviceId, accessToken]);

  // Fetch stream info for this device. Checks, in order: a Pi5 relay
  // assignment (device.stream-relay-id), then a direct RTSP source (camera
  // with its own RTSP server, e.g. TP-Link VIGI units — MediaMTX pulls from
  // it on demand under the "device/{deviceId}" path), then falls back to
  // the classic RTMP DeviceStreamKey flow ("live/{key}" path) if neither is
  // configured. Pi5-relay deliberately wins over direct-rtsp: assigning a
  // relay doesn't clear device.rtsp-url (it's still needed — just consumed
  // by the Pi5 instead of MediaMTX directly), so both can legitimately be
  // set at once for a relayed camera, and that's the expected steady state,
  // not a misconfiguration to "fix".
  const loadStreamInfo = useCallback(async () => {
    try {
      const relayRes = await fetch(`/api/stream/relay-source/${deviceId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const relayData = relayRes.ok ? await relayRes.json() : null;
      if (relayData?.relayDeviceId) {
        await loadRtmpBackedStreamInfo('pi5-relay');
        return;
      }

      const rtspRes = await fetch(`/api/stream/rtsp-source/${deviceId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const rtspData = rtspRes.ok ? await rtspRes.json() : null;

      if (rtspData?.rtspUrl) {
        // On-demand source: always "available" for viewing — MediaMTX
        // connects to the camera only once Watch Live is actually
        // pressed, so there's no publish-time signal to poll here (and
        // polling via a HEAD request would itself trigger a real
        // connection attempt to the camera every refresh).
        setStreamInfo({
          isLive: true,
          sourceMode: 'direct-rtsp',
          hlsUrl: `${HLS_BASE}/device/${deviceId}/index.m3u8`,
          whepUrl: `${WHEP_BASE}/device/${deviceId}/whep`,
          streamKey: '',
          rtmpUrl: null,
        });
        return;
      }

      await loadRtmpBackedStreamInfo('rtmp-key');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load stream info');
    }
  }, [deviceId, accessToken, loadRtmpBackedStreamInfo]);

  // Fetch recordings for this device
  const loadRecordings = useCallback(async () => {
    try {
      const res = await fetch(`/api/devices/${deviceId}/recordings`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        setRecordings(data);
      }
    } catch {
      // Recordings endpoint may not exist yet
    }
  }, [deviceId, accessToken]);

  // Populate the editable RTSP source field once on mount — deliberately
  // not part of the 10s-polled loadStreamInfo, so an in-progress edit here
  // never gets overwritten out from under the admin.
  const loadRtspUrlInput = useCallback(async () => {
    try {
      const res = await fetch(`/api/stream/rtsp-source/${deviceId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        setRtspUrlInput(data?.rtspUrl ?? '');
      }
    } catch {
      // Leave the field blank — not fatal, admin can still type a new URL
    }
  }, [deviceId, accessToken]);

  const handleSaveRtspSource = useCallback(async () => {
    setError(null);
    setSuccess(null);
    if (!rtspUrlInput.trim()) {
      setError('RTSP URL is required');
      return;
    }
    setRtspSaving(true);
    try {
      const res = await fetch(`/api/stream/rtsp-source/${deviceId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ rtspUrl: rtspUrlInput.trim() }),
      });
      if (!res.ok) throw new Error(`Failed to save RTSP source (HTTP ${res.status})`);
      setSuccess('RTSP source saved');
      await loadStreamInfo();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save RTSP source');
    } finally {
      setRtspSaving(false);
    }
  }, [deviceId, accessToken, rtspUrlInput, loadStreamInfo]);

  const handleClearRtspSource = useCallback(async () => {
    setError(null);
    setSuccess(null);
    setRtspSaving(true);
    try {
      const res = await fetch(`/api/stream/rtsp-source/${deviceId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error(`Failed to clear RTSP source (HTTP ${res.status})`);
      setRtspUrlInput('');
      setSuccess('RTSP source cleared');
      await loadStreamInfo();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to clear RTSP source');
    } finally {
      setRtspSaving(false);
    }
  }, [deviceId, accessToken, loadStreamInfo]);

  // Populate the relay-assignment picker once on mount — same
  // kept-separate-from-the-poll reasoning as loadRtspUrlInput above.
  const loadRelayAssignment = useCallback(async () => {
    try {
      const res = await fetch(`/api/stream/relay-source/${deviceId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        setRelayDeviceId(data?.relayDeviceId ?? '');
      }
    } catch {
      // Leave blank — not fatal, admin can still assign one
    }
  }, [deviceId, accessToken]);

  // Candidate devices for the relay picker — every device except this one.
  // Deliberately not filtered by DeviceType: the admin creates the Pi5's
  // Device row themselves (via the existing Add Device UI) and can name
  // its type however they like, so hardcoding an exact expected type name
  // here would be fragile. Loaded once, not on the 10s poll.
  const loadRelayOptions = useCallback(async () => {
    try {
      const res = await fetch('/api/devices', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (res.ok) {
        const data: { id: string; name: string }[] = await res.json();
        setRelayOptions(data.filter((d) => d.id !== deviceId));
      }
    } catch {
      // Leave empty — the picker will just show no options
    }
  }, [deviceId, accessToken]);

  const handleSaveRelayAssignment = useCallback(async () => {
    setError(null);
    setSuccess(null);
    if (!relayDeviceId) {
      setError('Choose a relay device first');
      return;
    }
    setRelaySaving(true);
    try {
      const res = await fetch(`/api/stream/relay-source/${deviceId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ relayDeviceId }),
      });
      if (!res.ok) throw new Error(`Failed to save relay assignment (HTTP ${res.status})`);
      setSuccess('Relay assignment saved');
      await loadStreamInfo();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save relay assignment');
    } finally {
      setRelaySaving(false);
    }
  }, [deviceId, accessToken, relayDeviceId, loadStreamInfo]);

  const handleClearRelayAssignment = useCallback(async () => {
    setError(null);
    setSuccess(null);
    setRelaySaving(true);
    try {
      const res = await fetch(`/api/stream/relay-source/${deviceId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error(`Failed to clear relay assignment (HTTP ${res.status})`);
      setRelayDeviceId('');
      setSuccess('Relay assignment cleared');
      await loadStreamInfo();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to clear relay assignment');
    } finally {
      setRelaySaving(false);
    }
  }, [deviceId, accessToken, loadStreamInfo]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await loadStreamInfo();
      await loadRecordings();
      await loadRtspUrlInput();
      await loadRelayAssignment();
      await loadRelayOptions();
      setLoading(false);
    };
    load();
  }, [loadStreamInfo, loadRecordings, loadRtspUrlInput, loadRelayAssignment, loadRelayOptions]);

  // Auto-refresh stream status every 10 seconds. Skipped while relayWaking
  // — startRelayWaitForLive runs its own tighter is-live poll during that
  // window, and running both at once would just produce confusing
  // overlapping state updates for no benefit.
  useEffect(() => {
    const interval = setInterval(() => {
      if (relayWaking) return;
      loadStreamInfo();
      if (subTab === 1) loadRecordings();
    }, 10000);
    return () => clearInterval(interval);
  }, [loadStreamInfo, loadRecordings, subTab, relayWaking]);

  // LL-HLS fallback, used when WHEP negotiation fails or the connection
  // drops after establishing (e.g. ICE couldn't traverse NAT).
  const startHlsFallback = useCallback(() => {
    console.warn('[live-stream] startHlsFallback called', { hasStreamInfo: !!streamInfo, hasVideoEl: !!liveVideoRef.current });
    if (!streamInfo) return;
    const video = liveVideoRef.current;
    if (!video) return;

    if (Hls.isSupported()) {
      const hls = new Hls({ enableWorker: true, lowLatencyMode: true });
      console.warn('[live-stream] hls.js loading source', streamInfo.hlsUrl);
      hls.loadSource(streamInfo.hlsUrl);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        console.warn('[live-stream] hls.js MANIFEST_PARSED, calling play()');
        video.play().catch((e) => console.warn('[live-stream] video.play() rejected', e));
      });
      hls.on(Hls.Events.ERROR, (_, data) => {
        console.warn('[live-stream] hls.js error', data);
        if (!data.fatal) return;

        // hls.js's own recommended recovery pattern (its README covers this
        // exact switch) — a fatal error does not mean unrecoverable. This
        // link is a Starlink hop (see root CLAUDE.md's network chain) with
        // real, expected transient packet-loss storms severe enough to
        // crash MediaMTX's muxer entirely ("too many reordered frames"),
        // which surfaces here as a fatal NETWORK_ERROR — previously this
        // just abandoned the hls.js instance and left the player dead until
        // a manual Stop/Watch Live, even once the source recovered a few
        // seconds later.
        switch (data.type) {
          case Hls.ErrorTypes.NETWORK_ERROR:
            console.warn('[live-stream] fatal network error, retrying load');
            hls.startLoad();
            break;
          case Hls.ErrorTypes.MEDIA_ERROR:
            console.warn('[live-stream] fatal media error, attempting recovery');
            hls.recoverMediaError();
            break;
          default:
            console.warn('[live-stream] unrecoverable hls.js error, giving up');
            setError('Stream playback error. The stream may have ended.');
            setIsLivePlaying(false);
            hls.destroy();
            hlsRef.current = null;
            break;
        }
      });
      hlsRef.current = hls;
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = streamInfo.hlsUrl;
      video.play().catch(() => {});
    } else {
      setError('Playback not supported in this browser.');
      setIsLivePlaying(false);
    }
  }, [streamInfo]);

  // Start live playback — WHEP (WebRTC) first for the lowest latency,
  // falling back to LL-HLS if negotiation fails or the connection drops.
  const startLivePlayback = useCallback(async () => {
    if (!streamInfo) return;
    const video = liveVideoRef.current;
    if (!video) return;

    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }

    setIsLivePlaying(true);

    try {
      const pc = await playWhep(video, streamInfo.whepUrl);
      pcRef.current = pc;

      // Real-world gotcha: with no STUN server configured, ICE frequently
      // never reaches 'connected' but also never reliably reaches 'failed'
      // either — the browser can sit in 'connecting'/'checking' indefinitely
      // even after MediaMTX has already given up server-side (its own
      // whepHandshakeTimeout, ~10s) and torn the session down. Relying on
      // onconnectionstatechange alone left viewers stuck on a dead WebRTC
      // attempt with no fallback ever firing. This timeout is a hard
      // backstop: if we're not actually connected within a few seconds past
      // MediaMTX's own timeout, force the fallback regardless of what
      // connectionState reports.
      let fellBack = false;
      const doFallback = (reason: string) => {
        console.warn('[live-stream] doFallback called', { reason, fellBack, sameConn: pcRef.current === pc });
        if (fellBack || pcRef.current !== pc) return;
        fellBack = true;
        pcRef.current = null;
        pc.close();
        startHlsFallback();
      };
      whepFallbackTimeoutRef.current = setTimeout(() => doFallback('timeout'), 13000);
      console.warn('[live-stream] WHEP negotiated, fallback timer armed', { whepUrl: streamInfo.whepUrl });

      pc.onconnectionstatechange = () => {
        console.warn('[live-stream] connectionState changed', pc.connectionState);
        if (pc.connectionState === 'connected' && whepFallbackTimeoutRef.current) {
          clearTimeout(whepFallbackTimeoutRef.current);
          whepFallbackTimeoutRef.current = null;
        } else if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
          if (whepFallbackTimeoutRef.current) {
            clearTimeout(whepFallbackTimeoutRef.current);
            whepFallbackTimeoutRef.current = null;
          }
          doFallback('connectionstatechange:' + pc.connectionState);
        }
      };
      pc.oniceconnectionstatechange = () => {
        console.warn('[live-stream] iceConnectionState changed', pc.iceConnectionState);
      };
      video.play().catch(() => {});
    } catch (err) {
      console.warn('WHEP playback failed, falling back to LL-HLS:', err);
      startHlsFallback();
    }
  }, [streamInfo, startHlsFallback]);

  // Pi5-relay's Watch Live equivalent of MediaMTX's own sourceOnDemand:
  // nothing is actually running until this fires POST .../start (the Pi5
  // is battery-powered, can't stream 24/7 — see the relay-source/start
  // endpoint's comment in StreamController), so this polls is-live on a
  // short interval — a self-rescheduling setTimeout chain (one ref,
  // matching whepFallbackTimeoutRef's single-timeout-backstop shape above,
  // rather than a separate setInterval + counter) — until either it comes
  // online or a generous deadline passes covering Pi5 wake + RTSP connect
  // + RTMP handshake + first keyframe. 35s is a starting guess, worth
  // tuning once real Pi5 cold-start latency is actually measured.
  const startRelayWaitForLive = useCallback(() => {
    const deadline = Date.now() + 35000;
    setRelayWaking(true);

    const poll = async () => {
      try {
        const res = await fetch(`/api/stream/is-live/${deviceId}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (res.ok && (await res.json()).isLive === true) {
          relayPollTimeoutRef.current = null;
          setRelayWaking(false);
          await startLivePlayback();
          return;
        }
      } catch {
        // keep polling
      }

      if (Date.now() >= deadline) {
        relayPollTimeoutRef.current = null;
        setRelayWaking(false);
        setError("Camera relay didn't come online — check the Pi5's power and network, then try again.");
        return;
      }

      relayPollTimeoutRef.current = setTimeout(poll, 2000);
    };

    poll();
  }, [deviceId, accessToken, startLivePlayback]);

  const handleWatchPi5Relay = useCallback(async () => {
    setError(null);
    if (relayPollTimeoutRef.current) {
      clearTimeout(relayPollTimeoutRef.current);
      relayPollTimeoutRef.current = null;
    }
    try {
      const res = await fetch(`/api/stream/relay-source/${deviceId}/start`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error(`Failed to start camera relay (HTTP ${res.status})`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to start camera relay');
      return;
    }
    startRelayWaitForLive();
  }, [deviceId, accessToken, startRelayWaitForLive]);

  const stopLivePlayback = useCallback(() => {
    if (whepFallbackTimeoutRef.current) {
      clearTimeout(whepFallbackTimeoutRef.current);
      whepFallbackTimeoutRef.current = null;
    }
    if (relayPollTimeoutRef.current) {
      clearTimeout(relayPollTimeoutRef.current);
      relayPollTimeoutRef.current = null;
    }
    setRelayWaking(false);
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    if (liveVideoRef.current) {
      liveVideoRef.current.pause();
      liveVideoRef.current.srcObject = null;
      liveVideoRef.current.src = '';
    }
    // Tell a pi5-relay device's Pi5 to stop pushing — the actual point of
    // gating this mode behind explicit start/stop at all (battery). Fired
    // regardless of whether playback ever actually started (e.g. Stop
    // clicked mid-wait) — safe/idempotent on the Pi5 side either way.
    // Best-effort: if this never arrives, the Pi5's own watchdog timeout
    // (MAX_STREAM_DURATION_SECONDS) is the real backstop, not this call.
    if (streamInfoRef.current?.sourceMode === 'pi5-relay') {
      fetch(`/api/stream/relay-source/${deviceId}/stop`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
      }).catch(() => {});
    }
    setIsLivePlaying(false);
  }, [deviceId, accessToken]);

  const revokeRecordingObjectUrl = useCallback(() => {
    if (recordingObjectUrlRef.current) {
      URL.revokeObjectURL(recordingObjectUrlRef.current);
      recordingObjectUrlRef.current = null;
    }
  }, []);

  // Play a recording (MP4 — native browser playback). rec.url points at an
  // [Authorize]-gated API endpoint (DevicesController.GetDeviceRecordingFile)
  // — a native <video src> GET can't carry the app's Authorization header,
  // so setting it directly 401s. Fetch the file ourselves (with the header)
  // and hand the video element a blob object URL instead, same pattern as
  // annotatorDetectionTest.ts's fetchImageBlob.
  const handlePlayRecording = useCallback(async (rec: Recording) => {
    // The <video> element only mounts once playingRecording is truthy (see
    // JSX below), so on the first-ever play of a session
    // recordingVideoRef.current is still null right here — set the state
    // first and read the ref back out only after the await, by which point
    // React has committed the render and the element genuinely exists.
    setPlayingRecording(rec.name);
    setLoadingRecording(true);
    try {
      const res = await fetch(rec.url, { headers: { Authorization: `Bearer ${accessToken}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const video = recordingVideoRef.current;
      if (!video) return;
      revokeRecordingObjectUrl();
      const objectUrl = URL.createObjectURL(blob);
      recordingObjectUrlRef.current = objectUrl;
      video.src = objectUrl;
      video.play().catch(() => {});
    } catch {
      setError('Failed to load recording');
      setPlayingRecording(null);
    } finally {
      setLoadingRecording(false);
    }
  }, [accessToken, revokeRecordingObjectUrl]);

  const stopRecordingPlayback = useCallback(() => {
    if (recordingVideoRef.current) {
      recordingVideoRef.current.pause();
      recordingVideoRef.current.src = '';
    }
    revokeRecordingObjectUrl();
    setLoadingRecording(false);
    setPlayingRecording(null);
  }, [revokeRecordingObjectUrl]);

  // Download a recording. Same auth constraint as playback above — a plain
  // <a href> to the API endpoint can't carry the Authorization header
  // either, so it 401s the same way. Fetch as a blob and trigger the
  // browser's save dialog via a throwaway anchor pointed at the object URL.
  const handleDownloadRecording = useCallback(async (rec: Recording) => {
    setDownloadingRecording(rec.name);
    try {
      const res = await fetch(rec.url, { headers: { Authorization: `Bearer ${accessToken}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = rec.name;
      link.click();
      URL.revokeObjectURL(objectUrl);
    } catch {
      setError('Failed to download recording');
    } finally {
      setDownloadingRecording(null);
    }
  }, [accessToken]);

  // Delete a recording — admin-only server-side (DevicesController.
  // DeleteDeviceRecording), confirmed here first since it's irreversible.
  // If it's the one currently playing, stop that playback first so we're
  // not left with a player pointed at a file that no longer exists.
  const handleDeleteRecording = useCallback(async (rec: Recording) => {
    if (!window.confirm(`Delete recording "${rec.name}"? This can't be undone.`)) return;
    if (playingRecording === rec.name) stopRecordingPlayback();
    setDeletingRecording(rec.name);
    try {
      const res = await fetch(rec.url, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await loadRecordings();
    } catch {
      setError('Failed to delete recording');
    } finally {
      setDeletingRecording(null);
    }
  }, [accessToken, playingRecording, stopRecordingPlayback, loadRecordings]);

  // Stop playback when switching tabs
  const handleTabChange = useCallback((_: React.SyntheticEvent, newValue: number) => {
    if (newValue !== 0) stopLivePlayback();
    if (newValue !== 1) stopRecordingPlayback();
    setSubTab(newValue);
    if (newValue === 1) loadRecordings();
  }, [stopLivePlayback, stopRecordingPlayback, loadRecordings]);

  // Cleanup on unmount. Reads streamInfoRef/isLivePlayingRef rather than
  // the state directly — this closure is built once at mount (empty dep
  // array) and only actually runs later, at real unmount, so the plain
  // state variables would still hold whatever they were AT MOUNT, not
  // whatever they've become by the time the tab is actually closed.
  useEffect(() => {
    return () => {
      if (hlsRef.current) hlsRef.current.destroy();
      if (pcRef.current) pcRef.current.close();
      if (recordingObjectUrlRef.current) URL.revokeObjectURL(recordingObjectUrlRef.current);
      if (relayPollTimeoutRef.current) clearTimeout(relayPollTimeoutRef.current);
      if (streamInfoRef.current?.sourceMode === 'pi5-relay' && isLivePlayingRef.current) {
        fetch(`/api/stream/relay-source/${deviceId}/stop`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${accessToken}` },
        }).catch(() => {});
      }
    };
  }, [deviceId, accessToken]);

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Tabs value={subTab} onChange={handleTabChange} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab
          label="Live"
          icon={streamInfo?.isLive ? <FiberManualRecordIcon sx={{ fontSize: 12, color: 'error.main' }} /> : <LiveTvIcon fontSize="small" />}
          iconPosition="start"
        />
        <Tab
          label={`Recordings${recordings.length > 0 ? ` (${recordings.length})` : ''}`}
          icon={<VideoLibraryIcon fontSize="small" />}
          iconPosition="start"
        />
        <Tab
          label="Source"
          icon={<SettingsInputAntennaIcon fontSize="small" />}
          iconPosition="start"
        />
      </Tabs>

      {/* ===== LIVE TAB ===== */}
      {subTab === 0 && (
        <Box>
          {/* RTSP source config (admin-only write) — for cameras that run
              their own RTSP server (e.g. TP-Link VIGI units) rather than
              pushing RTMP. Saving here is what MediaMTX actually pulls
              from on demand; switching this from blank to a URL (or back)
              is what flips the tab below between RTMP-push mode and
              RTSP-pull "Watch Live" mode after the next refresh. */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                RTSP Source (for cameras with their own RTSP server)
              </Typography>
              <TextField
                label="RTSP URL"
                placeholder="rtsp://user:pass@192.168.x.x:554/stream1"
                value={rtspUrlInput}
                onChange={(e) => setRtspUrlInput(e.target.value)}
                fullWidth
                size="small"
                InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
              />
            </CardContent>
            <CardActions sx={{ justifyContent: 'flex-end' }}>
              <Button size="small" disabled={rtspSaving || !rtspUrlInput} onClick={handleClearRtspSource}>
                Clear
              </Button>
              <Button size="small" variant="contained" disabled={rtspSaving} onClick={handleSaveRtspSource}>
                {rtspSaving ? 'Saving…' : 'Save'}
              </Button>
            </CardActions>

            <Divider />

            {/* Relay assignment — for a camera reached over a link too
                unreliable for MediaMTX to pull RTSP from directly (e.g. a
                remote property over Starlink/Tailscale). A battery-powered
                Pi5 pulls RTSP from the camera on its own solid local LAN
                instead and pushes RTMP to us, on demand. This doesn't
                replace the RTSP URL above — it's still needed, just read by
                the Pi5 instead of MediaMTX, so leave it set. */}
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                Relay via Pi5 (for cameras reached over an unreliable link)
              </Typography>
              <TextField
                select
                label="Relay device"
                value={relayDeviceId}
                onChange={(e) => setRelayDeviceId(e.target.value)}
                fullWidth
                size="small"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {relayOptions.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.name}
                  </MenuItem>
                ))}
              </TextField>
            </CardContent>
            <CardActions sx={{ justifyContent: 'flex-end' }}>
              <Button size="small" disabled={relaySaving || !relayDeviceId} onClick={handleClearRelayAssignment}>
                Clear
              </Button>
              <Button size="small" variant="contained" disabled={relaySaving} onClick={handleSaveRelayAssignment}>
                {relaySaving ? 'Saving…' : 'Save'}
              </Button>
            </CardActions>
          </Card>

          <Card>
          {streamInfo?.rtmpUrl && (
            <Box sx={{ p: 2, pb: 0 }}>
              <TextField
                label="RTMP Address (enter this in the camera/drone's RTMP settings)"
                value={streamInfo.rtmpUrl}
                InputProps={{
                  readOnly: true,
                  sx: { fontFamily: 'monospace', fontSize: 13 },
                  endAdornment: (
                    <Tooltip title="Copy">
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => navigator.clipboard.writeText(streamInfo.rtmpUrl!)}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ),
                }}
                fullWidth
                size="small"
              />
            </Box>
          )}
          {/* Bounded, responsive height (not aspect-ratio-derived) so the
              card's controls below stay in view on tall/large screens
              instead of the video pushing them past the viewport — the
              video itself uses object-fit: contain to letterbox within
              whatever box size results, so it's never cropped or stretched. */}
          <Box sx={{
            position: 'relative',
            backgroundColor: '#000',
            width: '100%',
            height: { xs: '40vh', sm: '50vh', md: '60vh' },
            maxHeight: 'calc(100vh - 260px)',
            minHeight: 200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <video
              ref={liveVideoRef}
              style={{ width: '100%', height: '100%', objectFit: 'contain', display: isLivePlaying ? 'block' : 'none' }}
              controls
              playsInline
              // Both playback paths call video.play() programmatically from
              // async callbacks (WHEP negotiation, hls.js MANIFEST_PARSED) —
              // far enough from the original click that browser autoplay
              // policy can silently reject an unmuted play() (caught by the
              // .catch() in each path, so it fails with no visible error).
              // Neither device path actually carries audio to this element
              // anyway (WHEP only exposes video+audio tracks if MediaMTX
              // sends them, and the HLS path is video-only — MediaMTX's own
              // muxer drops the G711 track for HLS), so muting is free here
              // and removes autoplay-block as a variable entirely.
              muted
            />
            {relayWaking && (
              <Box sx={{
                position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: 1, color: '#fff',
                backgroundColor: 'rgba(0,0,0,0.5)', pointerEvents: 'none',
              }}>
                <CircularProgress color="inherit" size={40} />
                <Typography variant="body2">Waking camera relay…</Typography>
              </Box>
            )}
            {!isLivePlaying && !relayWaking && (
              <Box sx={{ textAlign: 'center', color: '#666' }}>
                <LiveTvIcon sx={{ fontSize: 64, mb: 1 }} />
                <Typography variant="body1">
                  {streamInfo?.sourceMode === 'direct-rtsp'
                    ? 'Camera available — press Play to connect'
                    : streamInfo?.sourceMode === 'pi5-relay'
                    ? 'Camera available — press Play to wake the relay'
                    : streamInfo?.isLive ? 'Live stream available — press Play' : 'No active stream'}
                </Typography>
              </Box>
            )}
          </Box>
          <CardActions sx={{ justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {streamInfo?.isLive && streamInfo?.sourceMode === 'rtmp-key' && (
                <Chip
                  icon={<FiberManualRecordIcon sx={{ fontSize: 12 }} />}
                  label="LIVE"
                  color="error"
                  size="small"
                  variant="filled"
                />
              )}
            </Box>
            <Box>
              {streamInfo?.isLive && !isLivePlaying && !relayWaking && (
                <Button
                  startIcon={<PlayArrowIcon />}
                  variant="contained"
                  color="error"
                  onClick={streamInfo.sourceMode === 'pi5-relay' ? handleWatchPi5Relay : startLivePlayback}
                >
                  Watch Live
                </Button>
              )}
              {(isLivePlaying || relayWaking) && (
                <Button startIcon={<StopIcon />} variant="outlined" onClick={stopLivePlayback}>
                  Stop
                </Button>
              )}
            </Box>
          </CardActions>
          </Card>
        </Box>
      )}

      {/* ===== RECORDINGS TAB ===== */}
      {subTab === 1 && (
        <Box>
          {/* Playback area */}
          {playingRecording && (
            <Card sx={{ mb: 3 }}>
              <Box sx={{ backgroundColor: '#000', aspectRatio: '16/9', position: 'relative' }}>
                <video
                  ref={recordingVideoRef}
                  style={{ width: '100%', height: '100%' }}
                  controls
                  playsInline
                />
                {loadingRecording && (
                  <Box sx={{
                    position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center', gap: 1, color: '#fff',
                    backgroundColor: 'rgba(0,0,0,0.5)', pointerEvents: 'none',
                  }}>
                    <CircularProgress color="inherit" size={40} />
                    <Typography variant="body2">Downloading recording…</Typography>
                  </Box>
                )}
              </Box>
              <CardActions sx={{ justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  {playingRecording}
                </Typography>
                <Button startIcon={<StopIcon />} variant="outlined" size="small" onClick={stopRecordingPlayback}>
                  Stop
                </Button>
              </CardActions>
            </Card>
          )}

          {recordings.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
              <VideoLibraryIcon sx={{ fontSize: 48, mb: 1 }} />
              <Typography>No recordings yet</Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {recordings.map((rec) => (
                <Grid item xs={12} sm={6} md={4} key={rec.name}>
                  <Card variant="outlined" sx={{ border: playingRecording === rec.name ? 2 : 1, borderColor: playingRecording === rec.name ? 'primary.main' : 'divider' }}>
                    <CardContent sx={{ pb: 1 }}>
                      <Typography variant="body2" fontWeight="bold" noWrap title={rec.name}>
                        {rec.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatFileSize(rec.size)} · {new Date(rec.modified).toLocaleString()}
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button
                        size="small"
                        startIcon={<PlayArrowIcon />}
                        onClick={() => handlePlayRecording(rec)}
                        disabled={playingRecording === rec.name}
                      >
                        Play
                      </Button>
                      <Tooltip title="Download">
                        <IconButton
                          size="small"
                          onClick={() => handleDownloadRecording(rec)}
                          disabled={downloadingRecording === rec.name}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteRecording(rec)}
                          disabled={deletingRecording === rec.name}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      {/* ===== SOURCE TAB ===== */}
      {subTab === 2 && (
        <Box>
          {/* RTSP source config (admin-only write) — for cameras that run
              their own RTSP server (e.g. TP-Link VIGI units) rather than
              pushing RTMP. Saving here is what MediaMTX actually pulls
              from on demand; switching this from blank to a URL (or back)
              is what flips the Live tab between RTMP-push mode and
              RTSP-pull "Watch Live" mode after the next refresh. */}
          <Card>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                RTSP Source (for cameras with their own RTSP server)
              </Typography>
              <TextField
                label="RTSP URL"
                placeholder="rtsp://user:pass@192.168.x.x:554/stream1"
                value={rtspUrlInput}
                onChange={(e) => setRtspUrlInput(e.target.value)}
                fullWidth
                size="small"
                InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
              />
            </CardContent>
            <CardActions sx={{ justifyContent: 'flex-end' }}>
              <Button size="small" disabled={rtspSaving || !rtspUrlInput} onClick={handleClearRtspSource}>
                Clear
              </Button>
              <Button size="small" variant="contained" disabled={rtspSaving} onClick={handleSaveRtspSource}>
                {rtspSaving ? 'Saving…' : 'Save'}
              </Button>
            </CardActions>
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default LiveStreamTab;